import React from "react";
import { WidgetWrapper } from "../../../../components/WidgetWrapper/WidgetWrapper";
import { EditField } from "../../../../components/EditPanel/types";
import { useWidgetEditable } from "../../../../hooks/useWidgetEditable";
import { themeColor, customColor } from "../../../../types/color";
import { LoginDialogWidgetProps, LoginDialogWidgetState } from "./types";

const defaultState: LoginDialogWidgetState = {
  // Dialog container
  dialogBgColor: customColor("#ffffff"),
  dialogBorderRadius: 16,
  dialogPadding: 40,

  // Logo circle
  logoIcon: "default",
  logoContainerSize: 64,
  logoContainerBgColor: themeColor("primary"),

  // Title
  titleFontSize: 24,
  titleColor: themeColor("textPrimary"),

  // Subtitle
  subtitleFontSize: 16,
  subtitleColor: themeColor("textPrimary"),

  // Input field
  inputBorderRadius: 6,
  inputBorderColor: customColor("#dcdddc"),

  // Continue (primary) button
  primaryButtonBgColor: themeColor("textPrimary"),
  primaryButtonDisabledBgColor: customColor("#DCDCDC"),
  primaryButtonTextColor: customColor("#ffffff"),
  primaryButtonBorderRadius: 999,

  // Resend (secondary/outlined) button
  secondaryButtonBorderColor: themeColor("textPrimary"),
  secondaryButtonDisabledBorderColor: customColor("#DCDCDC"),
  secondaryButtonTextColor: themeColor("textPrimary"),
  secondaryButtonBorderRadius: 999,

  // Back / cancel text
  cancelTextColor: themeColor("textPrimary"),
};

const editFields: EditField[] = [
  // ── Dialog ────────────────────────────────────────────────────────────
  { key: "dialogBgColor", label: "Background", type: "color", group: "Dialog" },
  {
    key: "dialogBorderRadius",
    label: "Border Radius",
    type: "number",
    min: 0,
    max: 32,
    step: 1,
    group: "Dialog",
  },
  {
    key: "dialogPadding",
    label: "Padding",
    type: "number",
    min: 16,
    max: 60,
    step: 4,
    group: "Dialog",
  },

  // ── Logo ──────────────────────────────────────────────────────────────
  { key: "logoIcon", label: "Logo Image", type: "icon", group: "Logo" },
  {
    key: "logoContainerSize",
    label: "Size",
    type: "number",
    min: 32,
    max: 96,
    step: 4,
    group: "Logo",
  },
  { key: "logoContainerBgColor", label: "Background", type: "color", group: "Logo" },

  // ── Title ─────────────────────────────────────────────────────────────
  {
    key: "titleFontSize",
    label: "Font Size",
    type: "number",
    min: 16,
    max: 32,
    step: 1,
    group: "Title",
  },
  { key: "titleColor", label: "Color", type: "color", group: "Title" },

  // ── Subtitle ──────────────────────────────────────────────────────────
  {
    key: "subtitleFontSize",
    label: "Font Size",
    type: "number",
    min: 12,
    max: 20,
    step: 1,
    group: "Subtitle",
  },
  { key: "subtitleColor", label: "Color", type: "color", group: "Subtitle" },

  // ── Input ─────────────────────────────────────────────────────────────
  { key: "inputBorderColor", label: "Border Color", type: "color", group: "Input" },
  {
    key: "inputBorderRadius",
    label: "Border Radius",
    type: "number",
    min: 0,
    max: 24,
    step: 1,
    group: "Input",
  },

  // ── Continue Button ───────────────────────────────────────────────────
  { key: "primaryButtonBgColor", label: "Background (Active)", type: "color", group: "Continue Button" },
  { key: "primaryButtonDisabledBgColor", label: "Background (Disabled)", type: "color", group: "Continue Button" },
  { key: "primaryButtonTextColor", label: "Text Color", type: "color", group: "Continue Button" },
  {
    key: "primaryButtonBorderRadius",
    label: "Border Radius",
    type: "number",
    min: 0,
    max: 999,
    step: 1,
    group: "Continue Button",
  },

  // ── Resend Button ─────────────────────────────────────────────────────
  { key: "secondaryButtonBorderColor", label: "Border (Active)", type: "color", group: "Resend Button" },
  { key: "secondaryButtonDisabledBorderColor", label: "Border (Disabled)", type: "color", group: "Resend Button" },
  { key: "secondaryButtonTextColor", label: "Text Color", type: "color", group: "Resend Button" },
  {
    key: "secondaryButtonBorderRadius",
    label: "Border Radius",
    type: "number",
    min: 0,
    max: 999,
    step: 1,
    group: "Resend Button",
  },

  // ── Cancel ────────────────────────────────────────────────────────────
  { key: "cancelTextColor", label: "Back Text Color", type: "color", group: "Cancel" },
];

export const LoginDialogWidget: React.FC<LoginDialogWidgetProps> = ({
  widgetId,
  displayName,
  initialState = {},
  children,
}) => {
  const {
    state,
    isLoading,
    isSaving,
    handleEditChange,
    handleSave,
    themeColors,
    themeIcons,
    resolveColor,
    canSave,
  } = useWidgetEditable<LoginDialogWidgetState>(
    widgetId,
    { ...defaultState, ...initialState },
    editFields
  );

  const logoCustomIcon =
    state.logoIcon && state.logoIcon !== "default"
      ? themeIcons[state.logoIcon]
      : null;
  const resolvedLogoUrl = logoCustomIcon ? logoCustomIcon.url : null;

  if (isLoading) {
    return <></>;
  }

  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={editFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      themeColors={themeColors}
      themeIcons={themeIcons}
      triggerMode="persistent"
      triggerPosition="top-right"
      wrapperStyle={{ width: "100%" }}
    >
      {children(state, resolveColor, resolvedLogoUrl)}
    </WidgetWrapper>
  );
};
